import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { login, getMe } from '../services/api';

const Login = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [totpCode, setTotpCode] = useState('');
  const [requires2FA, setRequires2FA] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [recaptchaToken, setRecaptchaToken] = useState('');
  const [recaptchaReady, setRecaptchaReady] = useState(false);
  const recaptchaRef = useRef(null);
  const recaptchaWidgetId = useRef(null);
  const { loginUser } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    let attempts = 0;
    const maxAttempts = 50;
    
    const initRecaptcha = () => {
      if (window.grecaptcha && window.grecaptcha.render && recaptchaRef.current) {
        if (recaptchaWidgetId.current === null) {
          try {
            recaptchaWidgetId.current = window.grecaptcha.render(recaptchaRef.current, {
              sitekey: '6LfNLkAsAAAAAIMU7RC72QuojFy6lYCWq0hMEbQ9',
              callback: (token) => setRecaptchaToken(token),
              'expired-callback': () => setRecaptchaToken('')
            });
            setRecaptchaReady(true);
          } catch (e) {
            setRecaptchaReady(true);
          }
        }
        return true;
      }
      return false;
    };

    if (!initRecaptcha()) {
      const interval = setInterval(() => {
        attempts++;
        if (initRecaptcha() || attempts >= maxAttempts) {
          clearInterval(interval);
        }
      }, 100);
      return () => clearInterval(interval);
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!recaptchaToken && !requires2FA) {
      setError('Completa la verifica reCAPTCHA');
      return;
    }

    setLoading(true);

    try {
      const response = await login(username, password, requires2FA ? totpCode : null, recaptchaToken);

      if (response.data.requires_2fa) {
        setRequires2FA(true);
        setLoading(false);
        return;
      }

      const token = response.data.access_token;
      localStorage.setItem('token', token);

      const userResponse = await getMe();
      loginUser(token, userResponse.data);
      navigate('/');
    } catch (err) {
      setError(err.response?.data?.detail || 'Errore di login');
      if (window.grecaptcha && recaptchaWidgetId.current !== null) {
        window.grecaptcha.reset(recaptchaWidgetId.current);
        setRecaptchaToken('');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-container">
      <div className="login-box">
        <h2 className="login-title">LottoLab</h2>

        {error && <div className="alert alert-error">{error}</div>}

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label className="form-label">Username</label>
            <input
              type="text"
              className="form-input"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              required
              disabled={requires2FA}
            />
          </div>

          <div className="form-group">
            <label className="form-label">Password</label>
            <input
              type="password"
              className="form-input"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              disabled={requires2FA}
            />
          </div>

          {requires2FA && (
            <div className="form-group">
              <label className="form-label">Codice 2FA</label>
              <input
                type="text"
                className="form-input"
                value={totpCode}
                onChange={(e) => setTotpCode(e.target.value)}
                placeholder="123456"
                maxLength={6}
                required
                autoFocus
              />
            </div>
          )}

          {!requires2FA && (
            <div className="form-group" style={{ display: 'flex', justifyContent: 'center', minHeight: '78px' }}>
              <div ref={recaptchaRef}></div>
              {!recaptchaReady && (
                <div style={{ color: '#718096', fontSize: '0.9rem' }}>Caricamento reCAPTCHA...</div>
              )}
            </div>
          )}

          <button
            type="submit"
            className="btn btn-primary"
            style={{ width: '100%', marginTop: '10px' }}
            disabled={loading}
          >
            {loading ? 'Accesso...' : requires2FA ? 'Verifica' : 'Accedi'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default Login;
